import type { PropsWithChildren } from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useWidgetTokenStore } from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';
import { shuffle as shuffleArray } from 'es-toolkit';
import type { AudioItem, PeaksSettings } from '../../../types';
import { useCustomMetadataFetchers } from '../common';
import { player, radio, rss } from './source-types';

export interface ExtendedAudioItem extends AudioItem {
  hasTitle: boolean;
  hasAuthor: boolean;
  hasCaption: boolean;
  hasCaptionValue: boolean;
  _trackPeaksSettings?: PeaksSettings;
}

interface SourceContextValue {
  items: ExtendedAudioItem[];
  shuffle: boolean;
  setShuffle: (shuffle: boolean) => void;
  currentItemDescriptionId: number | null;
  setCurrentItemDescriptionId: (id: number | null) => void;
  currentItemId: number;
  setCurrentItemId: (id: number) => void;
  currentItem?: ExtendedAudioItem;
}

const SourceContext = createContext<SourceContextValue>({
  items: [],
  currentItemId: 0,
  currentItemDescriptionId: 0,
  shuffle: false,
  setShuffle: () => {},
  setCurrentItemDescriptionId: () => {},
  setCurrentItemId: () => {}
});

export const useSource = () => useContext(SourceContext);

const sortByDate = (data: ExtendedAudioItem[]) =>
  [...data].sort(({ date: firstDate }, { date: secondDate }) => {
    if (!firstDate && secondDate) {
      return 1;
    }

    if (!secondDate && firstDate) {
      return -1;
    }

    if (!firstDate && !secondDate) {
      return 0;
    }

    return new Date(firstDate!).getTime() - new Date(secondDate!).getTime();
  });

export function SourceProvider({ children }: PropsWithChildren) {
  const {
    shouldCollectMetadata,
    source: sources,
    sourceType,
    reversePlaylist,
    shuffleByDefault,
    playerParts,
    RSSMetadata
  } = useSettings();
  const widgetTokenStore = useWidgetTokenStore();
  const [items, setItems] = useState<ExtendedAudioItem[]>([]);
  const [currentItemId, setCurrentItemId] = useState<number>(0);
  const [currentItemDescriptionId, setCurrentItemDescriptionId] = useState<
    number | null
  >(null);
  const [shuffle, setShuffle] = useState(shuffleByDefault);

  const sourcesByType = { rss, player, radio }[sourceType];

  const [isWidgetTokenReady, setIsWidgetTokenReady] = useState(false);

  const prevShuffleRef = useRef(shuffle);
  const prevItemsRef = useRef<ExtendedAudioItem[]>();

  const currentItem = useMemo(
    () => items[currentItemId] ?? {},
    [items, currentItemId]
  );

  const { customMetadataFetchers } = useCustomMetadataFetchers();

  useEffect(() => {
    const widgetToken = widgetTokenStore.get();
    if (widgetToken) {
      setIsWidgetTokenReady(true);
    }
  }, [widgetTokenStore]);

  useEffect(() => {
    setShuffle(shuffleByDefault);
  }, [shuffleByDefault]);

  useEffect(() => {
    if (!isWidgetTokenReady && sourceType === 'rss') {
      return;
    }

    sourcesByType(sources, widgetTokenStore.get())?.then((response) => {
      const result = (() => {
        const filteredExtendedAudioItems = response.reduce<ExtendedAudioItem[]>(
          (acc, item) => {
            if (item) {
              const { id, title, author, metadataSettings, link } = item;
              const { fetcherType, collectionStatus } = metadataSettings ?? {};

              const isCollectingFetcherType = collectionStatus === 'pending';
              const hasCollectedFetcherType =
                collectionStatus === 'fulfilled' && !!fetcherType;
              const hasCustomFetcher = !!id && !!customMetadataFetchers?.[id];
              const hasCaptionValue =
                hasCollectedFetcherType ||
                isCollectingFetcherType ||
                hasCustomFetcher;

              let peaks = item.peaks;
              let _trackPeaksSettings = item._trackPeaksSettings;

              if (sourceType === 'rss' && RSSMetadata && link) {
                const metadataItem = RSSMetadata.find(
                  (meta) => meta.link === link
                );

                if (metadataItem) {
                  peaks = metadataItem.peaks;
                  _trackPeaksSettings = metadataItem._trackPeaksSettings;
                }
              }

              acc.push({
                ...item,
                peaks,
                _trackPeaksSettings,
                hasCaptionValue,
                hasTitle: playerParts.includes('title') && !!title,
                hasAuthor: playerParts.includes('author') && !!author,
                // Может вернуть true только для Radio Player
                hasCaption: !!shouldCollectMetadata && hasCaptionValue
              });
            }

            return acc;
          },
          []
        );

        if (sourceType === 'rss') {
          return shuffle
            ? shuffleArray(filteredExtendedAudioItems)
            : sortByDate(filteredExtendedAudioItems);
        }

        return shuffle
          ? shuffleArray(filteredExtendedAudioItems)
          : filteredExtendedAudioItems;
      })();

      const finalItems = reversePlaylist ? result.reverse() : result;
      setItems(finalItems);

      const hasShuffleChanged = prevShuffleRef.current !== shuffle;

      const isFirstItemChanged =
        prevItemsRef.current?.[0]?.link !== finalItems[0]?.link;
      const isItemsCountChanged =
        prevItemsRef.current?.length !== finalItems.length;
      const hasTemplateChanged =
        !hasShuffleChanged && (isFirstItemChanged || isItemsCountChanged);

      if (hasShuffleChanged && currentItem.link) {
        const newIndex = finalItems.findIndex(
          (item) => item.link === currentItem.link
        );
        setCurrentItemId(newIndex);
      }

      // Устанавливаем первый трек при переключении темплейтов
      if (hasTemplateChanged) {
        setCurrentItemId(0);
        prevItemsRef.current = finalItems;
      }

      if (hasShuffleChanged) {
        prevShuffleRef.current = shuffle;
      }
    });

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sourceType,
    shuffle,
    reversePlaylist,
    sourcesByType,
    isWidgetTokenReady,
    sources,
    playerParts,
    shouldCollectMetadata,
    customMetadataFetchers,
    RSSMetadata
  ]);

  useEffect(() => {
    const { title, author: artist, image } = currentItem ?? {};

    const hasMeta = !!title || !!artist || !!image;
    if (!hasMeta || !navigator.mediaSession) {
      return;
    }

    navigator.mediaSession.metadata = new MediaMetadata({
      title,
      artist,
      artwork: image ? [{ src: image }] : undefined
    });

    return () => {
      navigator.mediaSession.metadata = null;
    };
  }, [currentItem, sourceType]);

  const value = useMemo(
    () => ({
      items,
      setItems,
      currentItem: currentItem ?? {},
      currentItemId,
      setCurrentItemId,
      currentItemDescriptionId,
      setCurrentItemDescriptionId,
      shuffle,
      setShuffle
    }),
    [currentItem, currentItemDescriptionId, currentItemId, items, shuffle]
  );

  return (
    <SourceContext.Provider value={value}>{children}</SourceContext.Provider>
  );
}
